import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function LotsPage() {
  const { t } = useTranslation();
  const [batch, setBatch] = useState('');
  const [inStock, setInStock] = useState(true);
  const [expiringOnly, setExpiringOnly] = useState(false);

  const lots = useQuery({
    queryKey: ['inventory', 'lots', batch, inStock],
    queryFn: () => inventoryApi.listLots({ batchNumber: batch || undefined, inStock }).then((r) => r.items),
    enabled: !expiringOnly,
  });
  const expiring = useQuery({
    queryKey: ['inventory', 'expiring'],
    queryFn: () => inventoryApi.listExpiring({ days: 30 }).then((r) => r.items),
    enabled: expiringOnly,
  });

  const rows = expiringOnly ? (expiring.data || []) : (lots.data || []);
  const today = todayIso();

  const columns = [
    { key: 'sku', label: t('lot.col.product'), render: (r) => `${r.sku} · ${r.productName}` },
    { key: 'batch', label: t('lot.col.batch'), render: (r) => (r.batchNumber ? <span className="mono">{r.batchNumber}</span> : '-') },
    { key: 'warehouse', label: t('lot.col.warehouse'), render: (r) => r.warehouseCode || '-' },
    {
      key: 'expiry',
      label: t('lot.col.expiry'),
      render: (r) => (r.expiryDate
        ? <span style={{ color: r.expiryDate <= today ? 'var(--color-danger)' : 'inherit' }}>{formatDate(r.expiryDate)}</span>
        : '-'),
    },
    { key: 'remaining', label: t('lot.col.remaining'), align: 'right', render: (r) => r.remainingQuantity },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('lot.title')}</h1>
          <p className="muted">{t('lot.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant={expiringOnly ? 'primary' : 'secondary'} onClick={() => setExpiringOnly((v) => !v)}>
            {t('lot.action.expiring')}
          </Button>
        </div>
      </header>

      {!expiringOnly && (
        <div className="card" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ maxWidth: 260 }}>
            <span className="field-label">{t('lot.filter.batch')}</span>
            <input className="input" value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="LOT-…" />
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} />
            <span>{t('lot.filter.inStock')}</span>
          </label>
        </div>
      )}

      {expiringOnly && (
        <div className="card-grid">
          <MetricCard label={t('lot.metric.expiring')} value={String(rows.length)} trendVariant={rows.length > 0 ? 'warning' : 'success'} />
          <MetricCard label={t('lot.metric.expired')} value={String(rows.filter((r) => r.expiryDate <= today).length)} trendVariant="danger" />
        </div>
      )}

      <Table columns={columns} rows={rows} isLoading={expiringOnly ? expiring.isLoading : lots.isLoading} emptyMessage={t('lot.empty')} />
    </section>
  );
}
